import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { COURSES } from '../data/academicCatalog';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const unsubPerfilRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUsuario(user);
        iniciarPerfilRealTime(user.uid);
      } else {
        setUsuario(null);
        setPerfil(null);
        pararPerfilRealTime();
      }
      setCarregando(false);
    });
    return unsub;
  }, []);

  const iniciarPerfilRealTime = (uid) => {
    pararPerfilRealTime();
    const ref = doc(db, 'usuarios', uid);
    const unsubPerfil = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const dados = snap.data();
        if (dados.isAdmin || dados.role === 'admin') {
          dados.isAdmin = true;
          dados.role = 'admin';
          dados.allowedCourses = COURSES.map(course => course.id);
          dados.pacote = 3;
          const { MATERIAS } = require('../data/perfis');
          dados.materias = Object.keys(MATERIAS);
        }
        // Se bloqueado, faz logout automático
        if (dados.bloqueado && !dados.isAdmin) {
          signOut(auth);
          return;
        }
        setPerfil(dados);
      }
    });
    unsubPerfilRef.current = unsubPerfil;
  };

  const pararPerfilRealTime = () => {
    if (unsubPerfilRef.current) {
      unsubPerfilRef.current();
      unsubPerfilRef.current = null;
    }
  };

  const login = async (email, senha) => {
    const resultado = await signInWithEmailAndPassword(auth, email, senha);
    return resultado;
  };

  const logout = async () => {
    pararPerfilRealTime();
    return signOut(auth);
  };

  const atualizarMateriasUsuario = async (materias) => {
    if (!usuario) return;
    const ref = doc(db, 'usuarios', usuario.uid);
    await updateDoc(ref, { materias });
    setPerfil(prev => ({ ...prev, materias }));
  };

  const marcarPresenca = async () => {
    if (!usuario) return false;
    const hoje = new Date().toISOString().split('T')[0];
    const ref = doc(db, 'usuarios', usuario.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return false;
    const data = snap.data();
    const presencas = data.presencas || [];
    if (presencas.includes(hoje)) return 'ja_marcado';
    const novasPresencas = [...presencas, hoje];
    await updateDoc(ref, { presencas: novasPresencas });
    setPerfil(prev => ({ ...prev, presencas: novasPresencas }));
    return 'marcado';
  };

  const desmarcarPresenca = async () => {
    if (!usuario) return false;
    const hoje = new Date().toISOString().split('T')[0];
    const ref = doc(db, 'usuarios', usuario.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return false;
    const data = snap.data();
    const presencas = (data.presencas || []).filter(d => d !== hoje);
    await updateDoc(ref, { presencas });
    setPerfil(prev => ({ ...prev, presencas }));
    return 'desmarcado';
  };

  const calcularStreak = (presencas) => {
    if (!presencas || presencas.length === 0) return 0;
    const sorted = [...presencas].sort().reverse();
    const ontem = new Date();
    ontem.setDate(ontem.getDate() - 1);
    const ontemStr = ontem.toISOString().split('T')[0];
    const confirmados = sorted.filter(d => d <= ontemStr);
    if (confirmados.length === 0) return 0;
    let streak = 0;
    let dataAtual = ontemStr;
    for (let i = 0; i < confirmados.length; i++) {
      if (confirmados[i] === dataAtual) {
        streak++;
        const d = new Date(dataAtual);
        d.setDate(d.getDate() - 1);
        dataAtual = d.toISOString().split('T')[0];
      } else {
        break;
      }
    }
    return streak;
  };

  useEffect(() => {
    const atualizarStreak = async () => {
      if (!usuario || !perfil) return;
      const streakCalculado = calcularStreak(perfil.presencas || []);
      if (streakCalculado !== perfil.streak) {
        const ref = doc(db, 'usuarios', usuario.uid);
        await updateDoc(ref, { streak: streakCalculado });
        setPerfil(prev => ({ ...prev, streak: streakCalculado }));
      }
    };
    atualizarStreak();
  }, [perfil?.presencas]);

  const presencaHojeJaMarcada = () => {
    if (!perfil) return false;
    const hoje = new Date().toISOString().split('T')[0];
    return (perfil.presencas || []).includes(hoje);
  };

  const canAccessCourse = (courseId) => {
    if (!perfil) return false;
    if (perfil.isAdmin || perfil.role === 'admin') return true;
    if (Array.isArray(perfil.allowedCourses)) return perfil.allowedCourses.includes(courseId);
    return courseId === 'pcba-investigador' && (perfil.materias || []).length > 0;
  };

  const updateProfileMilestone = async (fields) => {
    if (!usuario) return;
    await updateDoc(doc(db, 'usuarios', usuario.uid), fields);
    setPerfil(previous => ({ ...previous, ...fields }));
  };

  const completeWelcome = () => updateProfileMilestone({ welcomeCompleted: true, welcomeCompletedAt: new Date().toISOString() });
  const completeIntro = () => updateProfileMilestone({ introCompleted: true, introCompletedAt: new Date().toISOString() });

  return (
    <AuthContext.Provider value={{
      usuario,
      perfil,
      carregando,
      login,
      logout,
      atualizarMateriasUsuario,
      marcarPresenca,
      desmarcarPresenca,
      calcularStreak,
      presencaHojeJaMarcada,
      canAccessCourse,
      completeWelcome,
      completeIntro
    }}>
      {children}
    </AuthContext.Provider>
  );
};
